import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatZAR } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, FileText, AlertCircle, Mail, Phone, MapPin, IdCard, Briefcase,
  Send, ExternalLink, Clock,
} from "lucide-react";

const SERVICE_TAG = {
  conveyancing:    "bg-blue-50 text-blue-800 border-blue-200",
  antenuptial:     "bg-rose-50 text-rose-800 border-rose-200",
  will:            "bg-amber-50 text-amber-800 border-amber-200",
  trust:           "bg-emerald-50 text-emerald-800 border-emerald-200",
  deceased_estate: "bg-zinc-100 text-zinc-700 border-zinc-300",
};

const STATUS_TONE = {
  draft_pending_review: "bg-amber-100 text-amber-800",
  pending_review:       "bg-amber-100 text-amber-800",
  sent:                 "bg-blue-100 text-blue-800",
  signed:               "bg-emerald-100 text-emerald-800",
  archived:             "bg-zinc-100 text-zinc-500",
};

export default function ClientDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => { api.get(`/clients/${id}`).then((r) => setData(r.data)); }, [id]);

  if (!data) return <div className="p-10 text-zinc-500">Loading…</div>;

  const { client, matters, documents, outstanding } = data;
  const services = [...new Set(matters.map((m) => m.service_label))];

  return (
    <div className="p-10 max-w-6xl mx-auto" data-testid="client-detail">
      <Link to="/admin/clients" className="text-sm text-zinc-500 hover:text-zinc-900 flex items-center gap-1 mb-6">
        <ArrowLeft className="h-3 w-3" /> Back to clients
      </Link>

      {/* ---------- Identity card ---------- */}
      <div className="bg-white border border-zinc-200 p-8 mb-8">
        <div className="flex items-start justify-between gap-8">
          <div>
            <div className="text-overline text-zinc-500">Client</div>
            <h1 className="font-serif text-4xl lg:text-5xl font-medium tracking-tight mt-1">
              {client.full_name}
            </h1>
            {services.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4">
                {services.map((s, i) => (
                  <span key={`${s ?? "unknown"}-${i}`} className="px-2 py-0.5 text-[11px] tracking-wide bg-zinc-100 text-zinc-700 rounded-sm">
                    {s || "—"}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm shrink-0">
            <Field icon={Mail}     label="Email"      value={client.email} mono />
            <Field icon={Phone}    label="Phone"      value={client.phone} />
            <Field icon={IdCard}   label="ID number"  value={client.id_number} mono />
            <Field icon={MapPin}   label="Address"    value={client.address} />
          </div>
        </div>
        <div className="flex gap-6 mt-8 pt-6 border-t border-zinc-100 text-sm">
          <Stat label="Matters"             value={data.matter_count} />
          <Stat label="Documents"           value={data.document_count} />
          <Stat
            label="Outstanding actions"
            value={outstanding.length}
            tone={outstanding.length ? "text-amber-700" : "text-zinc-900"}
            testid="client-outstanding-count"
          />
        </div>
      </div>

      {/* ---------- Outstanding actions ---------- */}
      {outstanding.length > 0 && (
        <Section
          title={`Outstanding actions  (${outstanding.length} matter${outstanding.length === 1 ? "" : "s"})`}
          testid="client-outstanding-section"
        >
          <div className="space-y-3">
            {outstanding.map((o) => (
              <OutstandingCard key={o.matter_id} item={o} />
            ))}
          </div>
        </Section>
      )}

      {/* ---------- Matters ---------- */}
      <Section title="Matters" testid="client-matters-section">
        <div className="bg-white border border-zinc-200">
          {matters.length === 0 ? (
            <div className="p-10 text-center text-zinc-500">
              No matters yet for this client.
            </div>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {matters.map((m) => {
                const mtype = m.matter_type || m.service_type;
                const tone = SERVICE_TAG[mtype] || "bg-zinc-50 text-zinc-700 border-zinc-200";
                return (
                  <li key={m.id}>
                    <Link
                      to={`/admin/matters/${m.id}`}
                      className="p-5 flex items-center justify-between gap-6 hover:bg-zinc-50 transition-colors"
                      data-testid={`client-matter-row-${m.id}`}
                    >
                      <div className="flex items-center gap-4 min-w-0 flex-1">
                        <Briefcase className="h-5 w-5 text-zinc-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="font-medium text-zinc-900">{m.service_label}</div>
                          <div className="text-xs text-zinc-500 mt-0.5">
                            Matter {m.id.slice(0, 8)} · created {new Date(m.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 shrink-0">
                        {m.doc_generation && !m.doc_generation.can_generate && (
                          <span className="text-[10px] tracking-widest uppercase text-amber-700">
                            Data incomplete
                          </span>
                        )}
                        <span className={`px-2 py-0.5 text-[11px] tracking-wide border ${tone}`}>
                          {(m.status || "").replace(/_/g, " ")}
                        </span>
                        {m.value > 0 && (
                          <span className="font-serif text-lg text-zinc-900">
                            {formatZAR(m.value)}
                          </span>
                        )}
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </Section>

      {/* ---------- Documents ---------- */}
      <Section title="Documents" testid="client-documents-section">
        <div className="bg-white border border-zinc-200">
          {documents.length === 0 ? (
            <div className="p-10 text-center text-zinc-500">
              No documents generated for this client yet.
            </div>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {documents.map((d) => (
                <li key={d.id}>
                  <Link
                    to={`/admin/matters/${d.matter_id}/documents/${d.id}`}
                    className="p-4 flex items-center justify-between gap-4 hover:bg-zinc-50 transition-colors"
                    data-testid={`client-doc-row-${d.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <FileText className="h-4 w-4 text-zinc-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-zinc-900 truncate">
                          {d.template_name}
                        </div>
                        <div className="text-xs text-zinc-500 mt-0.5">
                          Matter {d.matter_id.slice(0, 8)} · {new Date(d.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <span className={`px-2 py-0.5 text-[10px] tracking-wide rounded-sm ${
                      STATUS_TONE[d.status] || "bg-zinc-100 text-zinc-600"
                    }`}>
                      {(d.status || "").replace(/_/g, " ")}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <div className="mb-10" data-testid={testid}>
      <div className="text-overline text-zinc-500 mb-3">{title}</div>
      {children}
    </div>
  );
}

function Stat({ label, value, tone = "text-zinc-900", testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-overline text-zinc-500">{label}</div>
      <div className={`font-serif text-3xl mt-0.5 ${tone}`}>{value}</div>
    </div>
  );
}

function Field({ icon: Icon, label, value, mono }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-overline text-zinc-500">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className={`mt-1 text-zinc-900 ${mono ? "font-mono text-xs" : ""}`}>
        {value || <span className="text-zinc-400">—</span>}
      </div>
    </div>
  );
}

function OutstandingCard({ item }) {
  const portalUrl = item.portal_token
    ? `${window.location.origin}/client/${item.portal_token}`
    : null;
  const [sending, setSending] = useState(false);

  const copyPortalLink = () => {
    if (!portalUrl) { toast.error("No portal token for this matter"); return; }
    navigator.clipboard.writeText(portalUrl);
    toast.success("Portal link copied", {
      description: "Paste it into your client email or WhatsApp.",
    });
  };

  const sendSummary = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/admin/matters/${item.matter_id}/email-missing-summary`);
      toast.success("Missing-data summary sent", {
        description: `Simulated to ${data.to || "client"} — ${data.missing.length} field(s) listed.`,
      });
    } catch (e) {
      toast.error("Could not send summary", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSending(false);
    }
  };

  const hasMissing = item.missing && item.missing.length > 0;
  const hasTasks   = item.client_tasks && item.client_tasks.length > 0;
  const rs = item.reminder_summary;

  return (
    <div
      className="bg-white border border-amber-300 p-5"
      data-testid={`outstanding-card-${item.matter_id}`}
    >
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-zinc-900">
              {item.matter_label} — needs attention
            </span>
            <span className="text-[10px] tracking-widest uppercase text-zinc-500 font-normal">
              Matter {item.matter_id.slice(0, 8)}
            </span>
            {rs && rs.active_count > 0 && (
              <ReminderBadge summary={rs} matterId={item.matter_id} />
            )}
          </div>

          {hasMissing && (
            <div className="mt-3">
              <div className="text-[11px] tracking-widest uppercase text-zinc-500 font-medium">
                Missing
              </div>
              <ul className="text-sm text-zinc-800 mt-1 space-y-0.5 list-disc pl-5">
                {item.missing.map((it, i) => <li key={`m-${i}-${it}`}>{it}</li>)}
              </ul>
            </div>
          )}

          {hasTasks && (
            <div className="mt-3">
              <div className="text-[11px] tracking-widest uppercase text-zinc-500 font-medium">
                Open client tasks
              </div>
              <ul className="text-sm text-zinc-800 mt-1 space-y-0.5 list-disc pl-5">
                {item.client_tasks.map((it, i) => <li key={`t-${i}-${it}`}>{it}</li>)}
              </ul>
            </div>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={sendSummary}
              disabled={sending || !hasMissing}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider font-medium bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid={`outstanding-email-summary-${item.matter_id}`}
            >
              <Mail className="h-3 w-3" /> {sending ? "Sending…" : "Email missing-data summary"}
            </button>
            <button
              onClick={copyPortalLink}
              disabled={!portalUrl}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider font-medium border border-zinc-300 text-zinc-700 hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid={`outstanding-send-portal-${item.matter_id}`}
            >
              <Send className="h-3 w-3" /> Copy portal link
            </button>
            <Link
              to={`/admin/matters/${item.matter_id}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider font-medium border border-zinc-300 text-zinc-700 hover:bg-zinc-50"
              data-testid={`outstanding-open-matter-${item.matter_id}`}
            >
              <ExternalLink className="h-3 w-3" /> Open matter
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}


function ReminderBadge({ summary, matterId }) {
  const tone = summary.escalated
    ? "bg-rose-100 text-rose-800 border-rose-200"
    : "bg-zinc-100 text-zinc-700 border-zinc-300";
  const label = summary.latest_stage_label || `${summary.active_count} reminder`;
  const lastSent = summary.last_sent_at
    ? new Date(summary.last_sent_at).toLocaleDateString()
    : null;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] tracking-wide border ${tone}`}
      title={lastSent ? `Last sent ${lastSent}` : ""}
      data-testid={`reminder-badge-${matterId}`}
    >
      <Clock className="h-2.5 w-2.5" /> {label}
    </span>
  );
}
